import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import GetStandingAdjustments from "../queries/GetStandingAdjustment";
import {
    CREATE_POINT_ADJUSTMENT,
    DELETE_POINT_ADJUSTMENT,
    UPDATE_POINT_ADJUSTMENT,
} from "../mutations/StandingAdjustment";
import { toast } from "react-toastify";
import { confirmDelete } from "../../common/confirmDelete";
import { GlobalStyles } from "../../../constants/styles";

import PaginatedFlatTable from "../../pagination/PaginatedFlatTable";
import { formattedDateTime } from "../../../util/date";
import useThemeColors from "../../organization/portal/useThemeColors";
import { alphaFromHex } from "../../../util/colors";
import useTranslation from "../../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../../util/graphqlError";

const REASONS = [
    { key: "fairPlayBonus", value: "fair_play_bonus" },
    { key: "disciplinePenalty", value: "discipline_penalty" },
    { key: "manualAdjustment", value: "manual_adjustment" },
];

export default function Adjustment({ season, teams, stages }) {
    const colors = useThemeColors();
    const { t, language } = useTranslation();

    const [adjustments, setAdjustments] = useState([]);
    const [formData, setFormData] = useState({
        teamId: "",
        stageId: "",
        reason: "",
        reasonComment: "",
        points: "",
    });
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [getAdjustments] = GetStandingAdjustments();
    const [createAdjustment] = useMutation(CREATE_POINT_ADJUSTMENT);
    const [updateAdjustment] = useMutation(UPDATE_POINT_ADJUSTMENT);
    const [deleteAdjustment] = useMutation(DELETE_POINT_ADJUSTMENT);

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const loadedSeasonIdRef = useRef(null);

    const pageSize = 25;
    const [page, setPage] = useState(1);

    const pages = useMemo(() => {
        const total = adjustments?.length || 0;
        return Math.max(1, Math.ceil(total / pageSize));
    }, [adjustments?.length]);

    const pagedAdjustments = useMemo(() => {
        const list = Array.isArray(adjustments) ? adjustments : [];
        const start = (page - 1) * pageSize;
        return list.slice(start, start + pageSize);
    }, [adjustments, page]);

    useEffect(() => {
        if (page > pages) setPage(pages);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pages]);

    useEffect(() => {
        setPage(1);
    }, [season?.id]);

    const ui = useMemo(() => {
        return {
            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(var(--kbs-primary-rgb, 0,173,255), 0.22)",
                color: "#14151a",
                fontWeight: 900,
                fontSize: 12,
                lineHeight: 1,
                whiteSpace: "nowrap",
            },
            chipMuted: {
                background: "rgba(0,0,0,0.02)",
                color: "rgba(20,21,26,0.70)",
            },
            muted: {
                color: "rgba(20,21,26,0.60)",
                fontWeight: 700,
                fontSize: 12.5,
            },
            actionBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            actionBtnDanger: {
                border: "1px solid rgba(220,53,69,0.35)",
                background: "rgba(220,53,69,0.06)",
                color: "#dc3545",
            },
            actionBtnEdit: {
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.02)",
                color: "#14151a",
            },

            modalBackdrop: { backgroundColor: "rgba(0,0,0,0.55)" },
            modalDialog: { maxWidth: 520 },
            modalContent: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            modalHeader: {
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
            },
            modalTitleWrap: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
            modalBadge: {
                fontSize: 11.5,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            modalTitle: {
                margin: 0,
                fontWeight: 900,
                fontSize: 14,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            modalSub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            closeBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
                color: colors.textOnPrimary,
            },
            modalBody: { padding: 14, background: "#fff" },
            fieldLabel: {
                fontWeight: 900,
                fontSize: 12.5,
                color: "rgba(20,21,26,0.72)",
                marginBottom: 6,
            },
            input: { borderRadius: 12, border: "1px solid rgba(0,0,0,0.10)", fontWeight: 800 },
            textarea: { borderRadius: 12, border: "1px solid rgba(0,0,0,0.10)", fontWeight: 800 },
            modalFooter: {
                padding: "12px 14px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.01)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
            },
            btnGhost: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
            },
            btnPrimaryModal: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.10)",
                background: GlobalStyles.colors.canary,
                color: GlobalStyles.colors.woodsmoke,
                cursor: "pointer",
                lineHeight: 1,
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const disableActions = loading || submitting;

    const getReasonLabel = (value) => {
        const match = REASONS.find((r) => r.value === (value || "").toLowerCase());
        return match ? t(`adjustment.reasons.${match.key}`) : value;
    };

    const loadData = async () => {
        if (!season?.id) return;
        setLoading(true);
        try {
            const { data } = await getAdjustments({ variables: { seasonId: season.id } });
            setAdjustments(data?.pointAdjustments || []);
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "adjustment.toasts.loadError"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (loadedSeasonIdRef.current === season?.id) return;
        loadedSeasonIdRef.current = season?.id;
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [season?.id]);

    const handleInput = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData({
            teamId: "",
            stageId: "",
            reason: "",
            reasonComment: "",
            points: "",
        });
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        resetForm();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.teamId || !formData.stageId || !formData.points || !formData.reason) return;

        setSubmitting(true);
        try {
            if (editingId) {
                await updateAdjustment({ variables: { id: editingId, input: formData } });
                toast.success(t("adjustment.toasts.editedSuccess"), {
                    autoClose: 2500,
                    toastId: "adj_edited",
                });
            } else {
                await createAdjustment({ variables: { input: formData } });
                toast.success(t("adjustment.toasts.createdSuccess"), {
                    autoClose: 2500,
                    toastId: "adj_added",
                });
                setTimeout(() => window.location.reload(), 2500);
            }

            setShowModal(false);
            setEditingId(null);
            resetForm();
            await loadData();
            setPage(1);
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "adjustment.toasts.genericError"));
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (adj) => {
        setEditingId(adj.id);
        setFormData({
            teamId: adj.teamStanding?.team?.id || "",
            stageId: adj.teamStanding?.stage?.id || "",
            reason: (adj.reason || "").toLowerCase(),
            reasonComment: adj.reasonComment || "",
            points: adj.points ?? "",
        });
        setShowModal(true);
    };

    const handleDelete = (id) => {
        confirmDelete({
            title: t("adjustment.deleteConfirm.title"),
            message: t("adjustment.deleteConfirm.message"),
            confirmLabel: t("adjustment.deleteConfirm.confirmLabel"),
            cancelLabel: t("adjustment.deleteConfirm.cancelLabel"),
            danger: true,
            onConfirm: async () => {
                setSubmitting(true);
                try {
                    await deleteAdjustment({ variables: { id } });
                    toast.success(t("adjustment.toasts.deletedSuccess"), {
                        autoClose: 2500,
                        toastId: "adj_deleted",
                    });
                    await loadData();
                    setPage(1);
                } catch (err) {
                    toast.error(getLocalizedGraphQLError(err, t, "adjustment.toasts.deleteError"));
                } finally {
                    setSubmitting(false);
                }
            },
        });
    };

    return (
        <>
            <div id="kbola-adjustment-scope">
                <PaginatedFlatTable
                    title={t("adjustment.title")}
                    subtitle={t("adjustment.subtitle", {
                        season: season?.name || t("adjustment.values.thisSeason"),
                    })}
                    page={page}
                    pages={pages}
                    onPageChange={(p) => setPage(p)}
                    items={pagedAdjustments}
                    loading={loading}
                    loadingOverlay
                    onAdd={() => {
                        setEditingId(null);
                        resetForm();
                        setShowModal(true);
                    }}
                    headers={[
                        t("adjustment.headers.createdAt"),
                        t("adjustment.headers.stage"),
                        t("adjustment.headers.team"),
                        t("adjustment.headers.points"),
                        t("adjustment.headers.reason"),
                        t("adjustment.headers.comment"),
                        t("adjustment.headers.actions"),
                    ]}
                    renderRow={(adj) => {
                        const pts = Number(adj.points || 0);
                        const createdAt = adj?.createdAt
                            ? formattedDateTime(adj?.createdAt, undefined, language || "en")
                            : null;

                        const ptsChipStyle =
                            pts < 0
                                ? {
                                    ...ui.chip,
                                    borderColor: "rgba(220,53,69,0.25)",
                                    background: "rgba(220,53,69,0.08)",
                                    color: "#dc3545",
                                }
                                : {
                                    ...ui.chip,
                                    borderColor: "rgba(25,135,84,0.22)",
                                    background: "rgba(25,135,84,0.08)",
                                    color: "#198754",
                                };

                        return (
                            <tr key={adj.id}>
                                <td>
                                    <span style={ptsChipStyle}>
                                        <i className="fas fa-calendar-alt" />
                                        {createdAt}
                                    </span>
                                </td>

                                <td style={ui.muted}>
                                    <span className="kbola-pill kbola-pillBrand">
                                        {adj.teamStanding?.stage?.name || t("adjustment.values.empty")}
                                    </span>
                                </td>

                                <td style={{ fontWeight: 900, color: "#14151a" }}>
                                    {adj.teamStanding?.team?.name || t("adjustment.values.empty")}
                                </td>

                                <td>
                                    <span style={ptsChipStyle}>
                                        <i className="fas fa-bolt" />
                                        {pts}
                                    </span>
                                </td>

                                <td>
                                    <span style={{ ...ui.chip, ...ui.chipMuted }}>
                                        <i className="fas fa-tag" />
                                        {getReasonLabel(adj.reason)}
                                    </span>
                                </td>

                                <td style={ui.muted}>{adj.reasonComment || t("adjustment.values.noComment")}</td>

                                <td className="text-right">
                                    <div style={{ display: "inline-flex", gap: 8 }}>
                                        <button
                                            type="button"
                                            style={{
                                                ...ui.actionBtn,
                                                ...ui.actionBtnEdit,
                                                opacity: disableActions ? 0.65 : 1,
                                            }}
                                            title={t("adjustment.actions.edit")}
                                            aria-label={t("adjustment.actions.edit")}
                                            onClick={() => handleEdit(adj)}
                                            disabled={disableActions}
                                        >
                                            <i className="fas fa-edit" />
                                        </button>

                                        <button
                                            type="button"
                                            style={{
                                                ...ui.actionBtn,
                                                ...ui.actionBtnDanger,
                                                opacity: disableActions ? 0.65 : 1,
                                            }}
                                            title={t("adjustment.actions.delete")}
                                            aria-label={t("adjustment.actions.delete")}
                                            onClick={() => handleDelete(adj.id)}
                                            disabled={disableActions}
                                        >
                                            <i className="fas fa-trash" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    }}
                />

                <style>{`
          #kbola-adjustment-scope .kbola-table {
            min-width: 980px;
          }
          #kbola-adjustment-scope .kbola-pill {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 10px;
            border-radius: 999px;
            font-weight: 900;
            font-size: 12px;
            border: 1px solid rgba(0,0,0,0.10);
            line-height: 1;
            white-space: nowrap;
          }
          #kbola-adjustment-scope .kbola-pillBrand {
            background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.22);
            color: #14151a;
            border-color: rgba(0,0,0,0.10);
          }
        `}</style>
            </div>

            {showModal && (
                <div
                    id="kbola-adjustment-modal"
                    className="modal fade show d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={ui.modalBackdrop}
                >
                    <div className="modal-dialog" role="document" style={ui.modalDialog}>
                        <div className="modal-content" style={ui.modalContent}>
                            <div style={ui.modalHeader}>
                                <div style={ui.modalTitleWrap}>
                                    <span style={ui.modalBadge}>
                                        {editingId ? t("adjustment.badge.edit") : t("adjustment.badge.new")}
                                    </span>
                                    <div style={{ minWidth: 0 }}>
                                        <h5 style={ui.modalTitle}>
                                            {editingId
                                                ? t("adjustment.modal.editTitle")
                                                : t("adjustment.modal.addTitle")}
                                        </h5>
                                        <p style={ui.modalSub}>{t("adjustment.modal.subtitle")}</p>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    style={ui.closeBtn}
                                    onClick={closeModal}
                                    title={t("adjustment.actions.close")}
                                    aria-label={t("adjustment.actions.close")}
                                >
                                    <i className="fas fa-times" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="modal-body" style={ui.modalBody}>
                                    <div className="form-group">
                                        <label style={ui.fieldLabel}>{t("adjustment.fields.team")}</label>
                                        <select
                                            name="teamId"
                                            className="form-control"
                                            value={formData.teamId}
                                            onChange={handleInput}
                                            required
                                            disabled={submitting}
                                            style={ui.input}
                                        >
                                            <option value="">{t("adjustment.options.selectTeam")}</option>
                                            {teams.map((team) => (
                                                <option key={team.teamId} value={team.teamId}>
                                                    {team.teamName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label style={ui.fieldLabel}>{t("adjustment.fields.stage")}</label>
                                        <select
                                            name="stageId"
                                            className="form-control"
                                            value={formData.stageId}
                                            onChange={handleInput}
                                            required
                                            disabled={submitting}
                                            style={ui.input}
                                        >
                                            <option value="">{t("adjustment.options.selectStage")}</option>
                                            {stages.map((stage) => (
                                                <option key={stage.id} value={stage.id}>
                                                    {stage.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label style={ui.fieldLabel}>{t("adjustment.fields.points")}</label>
                                        <input
                                            name="points"
                                            type="number"
                                            className="form-control"
                                            value={formData.points}
                                            onChange={handleInput}
                                            required
                                            disabled={submitting}
                                            style={ui.input}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label style={ui.fieldLabel}>{t("adjustment.fields.reason")}</label>
                                        <select
                                            name="reason"
                                            className="form-control"
                                            value={formData.reason}
                                            onChange={handleInput}
                                            required
                                            disabled={submitting}
                                            style={ui.input}
                                        >
                                            <option value="">{t("adjustment.options.selectReason")}</option>
                                            {REASONS.map((r) => (
                                                <option key={r.value} value={r.value}>
                                                    {t(`adjustment.reasons.${r.key}`)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label style={ui.fieldLabel}>
                                            {t("adjustment.fields.commentOptional")}
                                        </label>
                                        <textarea
                                            name="reasonComment"
                                            className="form-control"
                                            value={formData.reasonComment}
                                            onChange={handleInput}
                                            disabled={submitting}
                                            rows={3}
                                            style={ui.textarea}
                                        />
                                    </div>
                                </div>

                                <div className="modal-footer" style={ui.modalFooter}>
                                    <button
                                        type="button"
                                        style={{ ...ui.btnGhost, opacity: submitting ? 0.7 : 1 }}
                                        onClick={closeModal}
                                        disabled={submitting}
                                    >
                                        {t("adjustment.actions.cancel")}
                                    </button>

                                    <button type="submit" style={ui.btnPrimaryModal} disabled={submitting}>
                                        {submitting ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                {t("adjustment.actions.saving")}
                                            </>
                                        ) : (
                                            <>
                                                <i className={`fas ${editingId ? "fa-save" : "fa-plus"}`} />
                                                {editingId
                                                    ? t("adjustment.actions.update")
                                                    : t("adjustment.actions.add")}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
